import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  createTrigger,
  fetchTriggers,
  updateTrigger
} from "../../../../store/triggerSlice";
import SimpleAutoComplete from "../../../../components/SimpleAutoComplete/SimpleAutoComplete";
import DateTimePickerComponent from "../../../../components/DateTimePickerComponent/DateTimePickerComponent";
import dayjs from "dayjs";

const validateDateRanges = (err, formData, formCreatedDate) => {
  const { periodFrom, periodTo, triggerDate } = formData;

  if (periodFrom && periodTo && dayjs(periodTo).isBefore(dayjs(periodFrom))) {
    err.periodTo = "Period To cannot be earlier than Period From";
  }

  if (triggerDate && formCreatedDate && dayjs(triggerDate).isBefore(dayjs(formCreatedDate), "day")) {
    const formattedDate = dayjs(formCreatedDate).format("DD/MM/YYYY");
    err.triggerDate = `Trigger date cannot be earlier than form creation date (${formattedDate})`;
  }
};

const validateByFormType = (err, formData, formType) => {
  const { assessmentName, assessmentDate, periodFrom, periodTo } = formData;

  if (formType === "CLASSROOM_ASSESSMENT_PERFORMANCE") {
    if (!assessmentName?.trim()) err.assessmentName = "Assessment name is required";
    if (!assessmentDate) err.assessmentDate = "Assessment date is required";
  } else {
    if (!periodFrom) err.periodFrom = "Period From is required";
    if (!periodTo) err.periodTo = "Period To is required";
  }
};

const TriggerModal = ({
  open,
  handleClose,
  initialData,
  formId,
  bootcampOptions = [],
  formType = undefined,
  formCreatedDate = undefined,
  onTriggerActionSuccess
}) => {
  const dispatch = useDispatch();

  const emptyForm = {
    bootcamps: [],
    assessmentName: "",
    assessmentDate: "",
    periodFrom: "",
    periodTo: "",
    triggerDate: "",
    triggerNow: false,
  };

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [initialSnapshot, setInitialSnapshot] = useState(null);


  useEffect(() => {
    if (initialData) {
      const populated = {
        ...emptyForm,
        ...initialData,
        bootcamps: initialData.bootcamps || [],
      };

      setFormData(populated);
      setInitialSnapshot(populated);
    } else {
      setFormData(emptyForm);
      setErrors({});
      setInitialSnapshot(null);
    }
  }, [initialData, open]);

  const isFutureTriggerDate = useMemo(() => {
    if (!formData.triggerDate) return false;

    const selectedDate = dayjs(formData.triggerDate).startOf('day');
    const today = dayjs().startOf('day');

    return selectedDate.isAfter(today);
  }, [formData.triggerDate]);


  useEffect(() => {
    if (isFutureTriggerDate && formData.triggerNow) {
      setFormData(prev => ({ ...prev, triggerNow: false }));
    }
  }, [isFutureTriggerDate, formData.triggerNow]);


  const calculateTriggerErrors = (formData, formType, formCreatedDate) => {
    const err = {};
    const { bootcamps, triggerDate } = formData;

    if (!bootcamps?.length) err.bootcamps = "Please select bootcamp";
    if (!triggerDate) err.triggerDate = "Trigger date is required";

    validateByFormType(err, formData, formType);

    validateDateRanges(err, formData, formCreatedDate);

    return err;
  };

  const validateAll = () => {
    const validationErrors = calculateTriggerErrors(formData, formType, formCreatedDate);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };


  const handleFieldChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const toUTCString = (dateValue, options = {}) => {
    if (!dateValue) return null;

    const { time = "00:00:00" } = options;
    const [hours, minutes, seconds] = time.split(":").map(Number);

    let dateObject;
    if (initialData) {
      dateObject = dateValue
    } else {
      dateObject = dayjs(dateValue).add(1, 'day');
    }
    return dayjs.utc(dateObject)
      .set("hour", hours)
      .set("minute", minutes)
      .set("second", seconds)
      .set("millisecond", 0)
      .toISOString();
  };





  const disableSave = useMemo(() => {
    const hasErrors = Object.values(errors).some(Boolean);
    const missingRequired =
      !formData.bootcamps?.length || !formData.triggerDate;

    if (hasErrors || missingRequired) return true;

    if (!initialSnapshot) return false;

    const normalize = (data) => ({
      ...data,
      bootcamps: data.bootcamps?.map((b) => b.id),
    });

    return (
      JSON.stringify(normalize(formData)) ===
      JSON.stringify(normalize(initialSnapshot))
    );
  }, [errors, formData, initialSnapshot]);


  const handleSubmit = () => {
    if (!validateAll()) return;

    let payload = {
      formId,
      bootcampId: formData.bootcamps[0].id,
      triggerDate: toUTCString(formData.triggerDate, { time: "00:00:00" }),
      triggerNow: formData.triggerNow,
    };

    if (formType === "CLASSROOM_ASSESSMENT_PERFORMANCE") {
      payload = {
        ...payload,
        assessmentName: formData.assessmentName || null,
        assessmentDate:
          toUTCString(formData.assessmentDate, { time: "23:59:59" }) || null,
      };
    } else {
      payload = {
        ...payload,
        periodFrom:
          toUTCString(formData.periodFrom, { time: "00:00:00" }) || null,
        periodTo:
          toUTCString(formData.periodTo, { time: "23:59:59" }) || null,
      };
    }

    if (initialData?.id) {
      payload.formTriggerId = initialData.id;
      delete payload.formId;

      dispatch(updateTrigger(payload)).then(() => {
        dispatch(fetchTriggers({ formId, page: 1 }));
        onTriggerActionSuccess();
      });
    } else {
      dispatch(createTrigger(payload)).then(() => {
        dispatch(fetchTriggers({ formId, page: 1 }));
        onTriggerActionSuccess();
      });
    }

    handleClose();
  };




  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" className="trigger-dialog">
      <DialogTitle className="trigger-title">
        <Typography className="trigger-title-text">
          {initialData ? "Edit" : "Create"}
        </Typography>
      </DialogTitle>

      <DialogContent className="trigger-content">
        <Box display="flex" flexDirection="column" gap={2} className="trigger-form-box">

          <SimpleAutoComplete
            label="Bootcamp"
            placeholder="Select Bootcamp"
            options={bootcampOptions}
            value={formData.bootcamps?.[0] ?? null}
            onChange={(event, newValue) => {
              setFormData((prev) => ({
                ...prev,
                bootcamps: newValue ? [newValue] : [],
              }));
              setErrors((prev) => ({ ...prev, bootcamps: undefined }));
            }}
            getOptionLabel={(option) => option?.name ?? ""}
            required
            error={!!errors?.bootcamps}
            helperText={errors?.bootcamps}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  <Box className="flex-options">
                    <Box>{option?.name}</Box>
                    {option?.name === "Select All" ? null : (
                      <Box>
                        {option?.startDate
                          ? dayjs(option?.startDate)?.format("DD/MM/YYYY")
                          : null}
                      </Box>
                    )}
                  </Box>
                </li>
              );
            }}
          />

          {formType === "CLASSROOM_ASSESSMENT_PERFORMANCE" && (
            <>
              <TextField
                label="Assessment Name"
                fullWidth
                size="small"
                color="secondary"
                required
                value={formData.assessmentName}
                error={!!errors.assessmentName}
                helperText={errors.assessmentName}
                onChange={(e) => handleFieldChange("assessmentName", e.target.value)}
              />

              <DateTimePickerComponent
                label="Assessment Date"
                required
                format="DD/MM/YYYY"
                value={formData.assessmentDate || null}
                onChange={(date) => handleFieldChange("assessmentDate", date)}
                views={["year", "month", "day"]}
                error={!!errors.assessmentDate}
                helperText={errors.assessmentDate}
                closeOnSelect
                enableClear
              />
            </>
          )}

          {formType !== "CLASSROOM_ASSESSMENT_PERFORMANCE" && (
            <>
              <DateTimePickerComponent
                label="Period From"
                format="DD/MM/YYYY"
                required
                value={formData.periodFrom || null}
                error={!!errors.periodFrom}
                helperText={errors.periodFrom}
                onChange={(date) => handleFieldChange("periodFrom", date)}
                views={["year", "month", "day"]}
                closeOnSelect
                enableClear
              />

              <DateTimePickerComponent
                label="Period To"
                required
                format="DD/MM/YYYY"
                value={formData.periodTo || null}
                onChange={(date) => handleFieldChange("periodTo", date)}
                views={["year", "month", "day"]}
                closeOnSelect
                enableClear
                minDate={formData.periodFrom || null}
                error={!!errors.periodTo}
                helperText={errors.periodTo}
              />
            </>
          )}

          <DateTimePickerComponent
            label="Schedule Date"
            format="DD/MM/YYYY"
            value={formData.triggerDate || null}
            onChange={(date) => handleFieldChange("triggerDate", date)}
            views={["year", "month", "day"]}
            closeOnSelect
            enableClear
            required
            error={!!errors.triggerDate}
            helperText={errors.triggerDate}
          />
          {!initialData && !isFutureTriggerDate && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.triggerNow}
                  onChange={(e) => handleFieldChange("triggerNow", e.target.checked)}
                  color="secondary"
                />
              }
              label={<Typography variant="font_14">Schedule Now</Typography>}
            />
          )}

        </Box>
      </DialogContent>

      <DialogActions className="trigger-actions">
        <Button
          onClick={handleClose}
          variant="outlined"
          color="secondary"
          className="btn-cancel"
        >
          <Typography variant="outlineBtnLabel">Cancel</Typography>
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          color="secondary"
          className="btn-save"
          disabled={disableSave}
        >
          <Typography variant="outlineBtnLabel">
            {initialData ? "Update" : "Save"}
          </Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TriggerModal;
